import Client, { ClientDTO } from '../../../models/Client';
import { FpAxios } from '@/services/FpAxios';
import { SmsApiDataWithPlatformRegulationsAccepted } from '@/models/LoginData';
import { Person, PersonDto } from '@/models/Person';
import Deal, { DealDTO } from '@/models/Deal';
import { Utils } from '@/services/utils/BasicUtils';
import LoanApplication, { LoanApplicationDTO } from '@/models/LoanApplication';
import { mapForValidation } from '@/modules/multiForm/shared/MultiformModels';
import { clone } from 'lodash-es';
import Activity, { ActivityDTO } from '@/models/Activity';
import { UserListElement, UserListElementDto } from '@/models/ppl/UserListElement';
import processProxy from '@/env/processProxy';

export interface RealEstateClientAgreement {
  type: 'AcceptsHomeEstateMarketing' | 'AcceptsHomeEstatePersonalDataProcessing';
  value: boolean;
  method: 'Sms' | 'Web';
}

export interface LoginResponse {
  authToken: string;
}

export interface HandoffCandidatesResponse {
  candidates: UserListElement[];
  existingOwner: UserListElement | null;
  activityLandId: number | null;
  activityDistrictId: number | null;
  alreadyShared: boolean;
}

export interface RealEstateRegistrationPayload {
  user: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    agreements: RealEstateClientAgreement[];
  };
  agentId: number;
  productType: string;
}

export interface RealEstateAccessStatuses {
  platformRegulationConsent: boolean,
  hasValidDeal: boolean,
  ownerRealEstateEnable: boolean
}

class ClientUserApi {
  private clientUserAxios = FpAxios.create({clientToken: true});
  private clientUserAxiosV3 = FpAxios.create({clientToken: true, baseURL: processProxy.VUE_APP_BASE_API_V3_URL});
  readonly baseUrl = 'client-auth';
  readonly clientsUrl = 'clients';

  async sendSms(phone: string, additionalParams?: Record<string, string>): Promise<void> {
    let url = `${this.baseUrl}/sms-code`;
    if (additionalParams) {
      url += `?${Object.entries(additionalParams).map(([key, value]) => `${key}=${value}`).join('&')}`;
    }
    await this.clientUserAxiosV3.post(url, { phone });
  }

  async checkSms(clientLoginData: SmsApiDataWithPlatformRegulationsAccepted): Promise<LoginResponse> {
    const response = await this.clientUserAxiosV3.post<LoginResponse>(`${this.baseUrl}/sms-code/verify`, {
      platformRegulationsAccepted: clientLoginData.platformRegulationsAccepted ?? undefined,
      phone: clientLoginData.phone,
      code: clientLoginData.smsCode
    });
    return response.data;
  }

  async verifyExchangeCode(exchangeCode: string): Promise<string> {
    const response = await this.clientUserAxios.post<string>(`${this.baseUrl}/token/exchange/verify`, { exchangeCode });
    return response.data;
  }

  async getUser(): Promise<Client> {
    const response = await this.clientUserAxios.get<ClientDTO>('shared/client');
    return new Client(response.data);
  }

  async getOwner(): Promise<Person> {
    const response = await this.clientUserAxios.get<PersonDto>('shared/owner');
    return new Person(response.data);
  }

  async editClientDataJSON(client: Client): Promise<ClientDTO> {
    client.clientDataJson = mapForValidation(client.clientDataJson!);
    const data = Utils.changeToNull(clone(client));
    const response = await this.clientUserAxios.put(`shared/clients/${client.id}/data`, data);
    return response.data;
  }

  async getLoanApplications(): Promise<LoanApplication[]> {
    const response = await this.clientUserAxios.get<LoanApplicationDTO[]>('shared/applications');
    return response.data.map(dto => new LoanApplication(dto));
  }

  async getActivity(): Promise<Activity> {
    const response = await this.clientUserAxios.get<ActivityDTO>('shared/activity');
    return new Activity(response.data);
  }

  async getDeal(): Promise<Deal> {
    const response = await this.clientUserAxios.get<DealDTO>('shared/deal');
    return new Deal(response.data);
  }

  async getRealEstateDeal(): Promise<Deal> {
    const response = await this.clientUserAxios.get<DealDTO>('shared/real-estate');
    return new Deal(response.data);
  }
  async getClientPanelTermsAndConditions(phone: string): Promise<boolean> {
    if (!phone || phone.trim().length === 0) { throw new Error('Numer telefonu jest wymagany'); }
    const response = await this.clientUserAxios.get<boolean>(`${this.clientsUrl}/platform-regulation/${phone}`);
    return response.data;
  }

  async prepareClientForClientPanel(phone: string): Promise<void> {
    if (!phone || phone.trim().length === 0) { throw new Error('Numer telefonu jest wymagany'); }
    await this.clientUserAxios.post(`shared/create-real-estate-deal-and-consents`);
  }
  async createClientAndDeal(agentId: number): Promise<void> {
    await this.clientUserAxios.post(`shared/create-real-estate-client-and-deal`, { agentId });
  }
  async registerRealEstateClient(smsCode: string, payload: RealEstateRegistrationPayload): Promise<string> {
    const response = await this.clientUserAxios.post<string>(`deals/create-with-real-estate-client/${smsCode}`, payload);
    return response.data;
  }
  async getRealEstateAccessStatus(): Promise<RealEstateAccessStatuses> {
    const response = await this.clientUserAxios.get<RealEstateAccessStatuses>('shared/real-estate-access-status');
    return response.data;
  }

  async getRealEstateAccessStatusForClient(clientId: number, phone?: Nullable<string>): Promise<Partial<RealEstateAccessStatuses>> {
    try {
      const response = await this.clientUserAxios.get<RealEstateAccessStatuses>(`shared/clients/${clientId}/real-estate-access-status`);
      return response.data;
    } catch {
      if (!phone?.trim()) {
        return {};
      }

      try {
        const platformRegulationConsent = await this.getClientPanelTermsAndConditions(phone);
        return { platformRegulationConsent };
      } catch {
        return {};
      }
    }
  }

  async sendExpertSms(phone: string): Promise<void> {
    await this.clientUserAxiosV3.post(`ekspert/smscode`, {
      phone
    });
  }

  async isPhoneBlacklisted(phone: string): Promise<boolean> {
    const response = await this.clientUserAxios.get<boolean>(`shared/client-panel/blacklist/${phone}`);
    return response.data;
  }

  async findOwners(clientId: number): Promise<UserListElement[]> {
    const response = await this.clientUserAxios.get<UserListElementDto[]>(`shared/clients/${clientId}/find-owners`);
    return response.data.map(dto => new UserListElement(dto));
  }

  async findHandoffCandidates(clientId: number, districtId: number | null, activityId: number | null): Promise<HandoffCandidatesResponse> {
    const response = await this.clientUserAxios.post<{
      candidates: UserListElementDto[];
      existingOwner: UserListElementDto | null;
      activityLandId: number | null;
      activityDistrictId: number | null;
      alreadyShared: boolean;
    }>(`shared/clients/${clientId}/handoff-candidates`, {
      districtId,
      activityId,
    });
    return {
      ...response.data,
      candidates: response.data.candidates.map(dto => new UserListElement(dto)),
      existingOwner: response.data.existingOwner
        ? new UserListElement(response.data.existingOwner)
        : null,
    };
  }
}

export default new ClientUserApi();
